/**
 * 身份列表页面
 * Identity Engine v9.0
 * 
 * 功能：
 * - 身份卡片网格展示
 * - 搜索和状态筛选
 * - 创建/编辑/删除/归档操作
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Plus, Search, Filter, ArrowLeft, Loader2, RefreshCw, Brain } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { useToast } from '@/shared/hooks/useToast'
import { IdentityApiService } from '@/modules/identity/services'
import {
  IdentityCard,
  IdentityCardSkeleton,
  CreateIdentityCard,
} from '@/modules/identity/components'
import type {
  Identity,
  IdentityStatus,
  IdentityListParams,
  CreateIdentityRequest,
} from '@/modules/identity/types'
import { IDENTITY_STATUS_CONFIG } from '@/modules/identity/types'

export default function IdentityList() {
  const navigate = useNavigate()
  const { showSuccess, showError } = useToast()

  // ==================== 状态 ====================
  const [identities, setIdentities] = useState<Identity[]>([])
  const [loading, setLoading] = useState(true)
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState<IdentityStatus | 'all'>('all')
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 1,
  })

  // 弹窗状态
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedIdentity, setSelectedIdentity] = useState<Identity | null>(null)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // 创建表单
  const [createForm, setCreateForm] = useState<CreateIdentityRequest>({
    name: '',
    description: '',
  })

  // ==================== 数据加载 ====================
  const loadIdentities = useCallback(async () => {
    setLoading(true)
    try {
      const params: IdentityListParams = {
        page: pagination.page,
        page_size: pagination.page_size,
      }
      if (searchText) {
        params.name = searchText
      }
      if (statusFilter !== 'all') {
        params.status = statusFilter
      }

      const response = await IdentityApiService.list(params)
      // 防御性编程：确保 data 和 pagination 有默认值
      setIdentities(response.data?.data || [])
      setPagination(prev => ({
        ...prev,
        total: response.data?.pagination?.total ?? 0,
        total_pages: response.data?.pagination?.total_pages ?? 1,
      }))
    } catch (error) {
      console.error('Failed to load identities:', error)
      showError('无法获取身份列表，请稍后重试', '加载失败')
    } finally {
      setLoading(false)
    }
  }, [pagination.page, pagination.page_size, searchText, statusFilter, showError])

  useEffect(() => {
    loadIdentities()
  }, [loadIdentities])

  // ==================== 操作处理 ====================
  const handleCardClick = useCallback((identity: Identity) => {
    navigate(`/identities/${identity.id}`)
  }, [navigate])

  const handleCreate = useCallback(async () => {
    if (!createForm.name.trim()) {
      showError('身份名称不能为空', '请输入名称')
      return
    }

    setCreating(true)
    try {
      const response = await IdentityApiService.create(createForm)
      showSuccess(`身份「${response.data.name}」已创建`, '创建成功')
      setCreateDialogOpen(false)
      setCreateForm({ name: '', description: '' })
      // 跳转到详情页
      navigate(`/identities/${response.data.id}`)
    } catch (error) {
      console.error('Failed to create identity:', error)
      showError('无法创建身份，请稍后重试', '创建失败')
    } finally {
      setCreating(false)
    }
  }, [createForm, navigate, showSuccess, showError])

  const handleArchive = useCallback(async (identity: Identity) => {
    try {
      await IdentityApiService.archive(identity.id)
      showSuccess(`身份「${identity.name}」已归档`, '已归档')
      loadIdentities()
    } catch (error) {
      console.error('Failed to archive identity:', error)
      showError('操作失败', '归档失败')
    }
  }, [loadIdentities, showSuccess, showError])

  const handleUnarchive = useCallback(async (identity: Identity) => {
    try {
      await IdentityApiService.unarchive(identity.id)
      showSuccess(`身份「${identity.name}」已恢复`, '已恢复')
      loadIdentities()
    } catch (error) {
      console.error('Failed to unarchive identity:', error)
      showError('操作失败', '恢复失败')
    }
  }, [loadIdentities, showSuccess, showError])

  const handleDeleteClick = useCallback((identity: Identity) => {
    setSelectedIdentity(identity)
    setDeleteDialogOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedIdentity) return

    setDeleting(true)
    try {
      await IdentityApiService.delete(selectedIdentity.id)
      showSuccess(`身份「${selectedIdentity.name}」已删除`, '已删除')
      setDeleteDialogOpen(false)
      setSelectedIdentity(null)
      loadIdentities()
    } catch (error) {
      console.error('Failed to delete identity:', error)
      showError('无法删除身份，请稍后重试', '删除失败')
    } finally {
      setDeleting(false)
    }
  }, [selectedIdentity, loadIdentities, showSuccess, showError])

  const handleSearch = useCallback((value: string) => {
    setSearchText(value)
    setPagination(prev => ({ ...prev, page: 1 }))
  }, [])

  const handleStatusFilter = useCallback((value: string) => {
    setStatusFilter(value as IdentityStatus | 'all')
    setPagination(prev => ({ ...prev, page: 1 }))
  }, [])

  // ==================== 渲染 ====================
  return (
    <div className="min-h-screen bg-slate-50">
      {/* 顶部导航 */}
      <header className="h-14 border-b bg-white flex items-center justify-between px-6 sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-violet-600" />
            <span className="font-semibold text-lg">身份引擎</span>
          </div>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          创建身份
        </Button>
      </header>

      {/* 主内容区 */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* 搜索和筛选 */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="搜索身份名称..."
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={handleStatusFilter}>
            <SelectTrigger className="w-[140px]">
              <Filter className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue placeholder="状态筛选" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部状态</SelectItem>
              {Object.entries(IDENTITY_STATUS_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>
                  {config.icon} {config.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={() => loadIdentities()}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* 统计信息 */}
        <div className="text-sm text-slate-500 mb-4">
          共 {pagination.total} 个身份
          {statusFilter !== 'all' && (
            <span className="ml-2 text-violet-600">
              (筛选: {IDENTITY_STATUS_CONFIG[statusFilter]?.label})
            </span>
          )}
        </div>

        {/* 卡片网格 */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <IdentityCardSkeleton key={i} />
            ))}
          </div>
        ) : identities.length === 0 ? (
          <div className="text-center py-20">
            <Brain className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">
              {searchText || statusFilter !== 'all' ? '没有找到匹配的身份' : '还没有创建身份'}
            </h3>
            <p className="text-sm text-slate-400 mb-6">
              {searchText || statusFilter !== 'all'
                ? '尝试调整搜索条件或筛选器'
                : '创建你的第一个 AI 数字分身吧'}
            </p>
            {!searchText && statusFilter === 'all' && (
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                创建身份
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <CreateIdentityCard onClick={() => setCreateDialogOpen(true)} />
            {identities.map((identity) => (
              <IdentityCard
                key={identity.id}
                identity={identity}
                onClick={handleCardClick}
                onArchive={handleArchive}
                onUnarchive={handleUnarchive}
                onDelete={handleDeleteClick}
              />
            ))}
          </div>
        )}

        {/* 分页 */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              上一页
            </Button>
            <span className="text-sm text-slate-500">
              {pagination.page} / {pagination.total_pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              下一页
            </Button>
          </div>
        )}
      </main>

      {/* 创建身份弹窗 */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>创建新身份</DialogTitle>
            <DialogDescription>
              创建一个新的 AI 数字分身，之后可以上传语料进行人格学习
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">名称 *</Label>
              <Input
                id="name"
                placeholder="输入身份名称"
                value={createForm.name}
                onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">描述</Label>
              <Textarea
                id="description"
                placeholder="描述这个身份的特点..."
                value={createForm.description || ''}
                onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              创建
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 删除确认弹窗 */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除身份「{selectedIdentity?.name}」吗？此操作无法撤销，所有语料和配置都将被永久删除。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
            >
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
